import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { twoFAApi } from '../../lib/api';
import { toast } from 'sonner';
import {
    KeyRound, Shield, Mail, Smartphone, Loader2, Copy, Check, AlertTriangle, RotateCcw,
} from 'lucide-react';

export function MyProfileDialog({ open, onOpenChange }) {
    const [tab, setTab] = useState('password');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="my-profile-dialog">
                <DialogHeader>
                    <DialogTitle>Mein Profil</DialogTitle>
                    <DialogDescription>
                        Passwort verwalten und Zwei-Faktor-Authentifizierung einrichten.
                    </DialogDescription>
                </DialogHeader>
                <Tabs value={tab} onValueChange={setTab} className="mt-2">
                    <TabsList className="grid grid-cols-2">
                        <TabsTrigger value="password" data-testid="profile-tab-password">
                            <KeyRound className="h-4 w-4 mr-1.5" /> Passwort
                        </TabsTrigger>
                        <TabsTrigger value="2fa" data-testid="profile-tab-2fa">
                            <Shield className="h-4 w-4 mr-1.5" /> 2FA
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="password" className="mt-4">
                        <ChangePasswordForm onDone={() => onOpenChange(false)} />
                    </TabsContent>
                    <TabsContent value="2fa" className="mt-4">
                        <TwoFactorSection />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}

function ChangePasswordForm({ onDone }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (next.length < 8) { toast.error('Neues Passwort muss min. 8 Zeichen haben'); return; }
        if (next !== confirm) { toast.error('Passwörter stimmen nicht überein'); return; }
        setLoading(true);
        try {
            await twoFAApi.changePassword(current, next);
            toast.success('Passwort geändert');
            setCurrent(''); setNext(''); setConfirm('');
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler beim Ändern');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <div>
                <Label className="text-xs">Aktuelles Passwort</Label>
                <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" />
            </div>
            <div>
                <Label className="text-xs">Neues Passwort (min. 8 Zeichen)</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" />
            </div>
            <div>
                <Label className="text-xs">Neues Passwort wiederholen</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" />
            </div>
            <Button onClick={submit} disabled={loading || !current || !next} className="mt-2 self-end" data-testid="pw-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Passwort ändern
            </Button>
        </div>
    );
}

function TwoFactorSection() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [setupView, setSetupView] = useState(null); // null | 'totp' | 'codes'
    const [totpData, setTotpData] = useState(null); // {qr_code, secret}
    const [totpCode, setTotpCode] = useState('');
    const [backupCodes, setBackupCodes] = useState(null);
    const [copied, setCopied] = useState(false);
    const [pwForDisable, setPwForDisable] = useState('');
    const [disableMode, setDisableMode] = useState(null); // null | 'all' | 'totp' | 'email'

    const reload = async () => {
        try { setStatus((await twoFAApi.status()).data); } catch { /* */ }
    };

    useEffect(() => { reload(); }, []);

    const startSetup = (method) => async () => {
        setLoading(true);
        try {
            if (method === 'email') {
                const r = await twoFAApi.setupEmail();
                if (r.data.backup_codes) {
                    setBackupCodes(r.data.backup_codes);
                    setSetupView('codes');
                    toast.success('E-Mail-2FA aktiviert. Beim nächsten Login wird ein Code an Ihre Mail gesendet.');
                } else {
                    toast.success('E-Mail-2FA als zusätzliche Methode aktiviert.');
                }
                await reload();
            } else {
                const r = await twoFAApi.setupTotp();
                setTotpData(r.data);
                setSetupView('totp');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Setup fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    const confirmTotp = async () => {
        setLoading(true);
        try {
            const r = await twoFAApi.confirmTotp(totpCode);
            if (r.data.backup_codes) {
                setBackupCodes(r.data.backup_codes);
                setSetupView('codes');
            } else {
                setSetupView(null);
                setTotpData(null);
            }
            setTotpCode('');
            toast.success('Authenticator-App als 2FA-Methode aktiviert');
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Code ungültig');
        } finally {
            setLoading(false);
        }
    };

    const disable2FA = async () => {
        if (!pwForDisable) { toast.error('Passwort eingeben'); return; }
        const what = disableMode === 'all' ? 'die komplette 2FA' : `nur die ${disableMode === 'totp' ? 'Authenticator-App' : 'E-Mail-Methode'}`;
        if (!window.confirm(`${what} wirklich deaktivieren?`)) return;
        setLoading(true);
        try {
            if (disableMode === 'all') {
                await twoFAApi.disable(pwForDisable);
                toast.success('2FA vollständig deaktiviert');
            } else {
                await twoFAApi.disableMethod(disableMode, pwForDisable);
                toast.success(`Methode entfernt`);
            }
            setPwForDisable('');
            setDisableMode(null);
            setSetupView(null); setTotpData(null); setBackupCodes(null);
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally {
            setLoading(false);
        }
    };

    const regenerate = async () => {
        if (!window.confirm('Neue Backup-Codes erzeugen? Alte werden ungültig.')) return;
        setLoading(true);
        try {
            const r = await twoFAApi.regenerate();
            setBackupCodes(r.data.backup_codes);
            setSetupView('codes');
            toast.success('Neue Backup-Codes erzeugt');
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally {
            setLoading(false);
        }
    };

    const copyCodes = () => {
        if (!backupCodes) return;
        navigator.clipboard.writeText(backupCodes.join('\n'));
        setCopied(true);
        toast.success('Backup-Codes kopiert');
        setTimeout(() => setCopied(false), 2000);
    };

    if (!status) return <div className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Lade Status…</div>;

    // Backup-Codes-Anzeige (nach Setup oder Regenerate)
    if (setupView === 'codes' && backupCodes) {
        return (
            <div className="flex flex-col gap-3" data-testid="2fa-backup-codes-view">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex gap-2 items-start text-xs">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold text-amber-900">Wichtig: Diese Codes werden nur EINMAL angezeigt.</p>
                        <p className="text-amber-800">Bitte ausdrucken oder sicher speichern – jeder Code ist genau 1× verwendbar bei Verlust des 2. Faktors.</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                    {backupCodes.map((c, i) => (
                        <div key={i} className="px-3 py-2 bg-gray-50 rounded border text-gray-800">{c}</div>
                    ))}
                </div>
                <div className="flex gap-2 self-end">
                    <Button variant="outline" size="sm" onClick={copyCodes} data-testid="copy-backup-codes">
                        {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                        {copied ? 'Kopiert' : 'Alle kopieren'}
                    </Button>
                    <Button size="sm" onClick={() => { setSetupView(null); setBackupCodes(null); }} data-testid="2fa-codes-done">
                        Verstanden
                    </Button>
                </div>
            </div>
        );
    }

    // TOTP Setup-View (QR + Code-Eingabe)
    if (setupView === 'totp' && totpData) {
        return (
            <div className="flex flex-col gap-3" data-testid="2fa-totp-setup">
                <p className="text-sm">Scannen Sie den QR-Code mit Ihrer Authenticator-App (z.B. Google Authenticator, 1Password, Authy).</p>
                <div className="flex justify-center bg-white p-3 rounded border">
                    <img src={totpData.qr_code} alt="TOTP QR-Code" className="h-48 w-48" />
                </div>
                <p className="text-xs text-muted-foreground">
                    Manuell: <span className="font-mono select-all">{totpData.secret}</span>
                </p>
                <div>
                    <Label className="text-xs">6-stelliger Code aus der App</Label>
                    <Input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" className="font-mono tracking-widest" data-testid="2fa-totp-code-input" />
                </div>
                <div className="flex gap-2 self-end">
                    <Button variant="outline" size="sm" onClick={() => { setSetupView(null); setTotpData(null); setTotpCode(''); }}>Abbrechen</Button>
                    <Button size="sm" onClick={confirmTotp} disabled={totpCode.length !== 6 || loading} data-testid="2fa-totp-confirm">
                        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                        Bestätigen & aktivieren
                    </Button>
                </div>
            </div>
        );
    }

    const methods = status.methods || (status.method ? [status.method] : []);
    const hasTotp = methods.includes('totp');
    const hasEmail = methods.includes('email');

    return (
        <div className="flex flex-col gap-3" data-testid="2fa-methods-view">
            <p className="text-sm text-muted-foreground">
                Sie können <strong>beide</strong> Methoden parallel aktivieren — beim Login wählen Sie dann frei,
                welche Sie nutzen möchten. Backup-Codes funktionieren als Notfall-Zugang, falls Sie auf keine
                der aktiven Methoden zugreifen können.
            </p>
            {status.enabled && (
                <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2 text-xs">
                    <Shield className="h-4 w-4 text-emerald-600" />
                    <span className="text-emerald-900">
                        2FA aktiv · {status.backup_codes_remaining} Backup-Code(s) übrig
                    </span>
                </div>
            )}
            <div className="flex flex-col gap-2">
                <div className="flex items-start gap-3 rounded-lg border p-3" data-testid="2fa-method-totp">
                    <div className={`p-2 rounded-md ${hasTotp ? 'bg-emerald-100' : 'bg-muted'}`}>
                        <Smartphone className={`h-4 w-4 ${hasTotp ? 'text-emerald-700' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                        <div className="font-medium text-sm flex items-center gap-2">
                            Authenticator-App (TOTP)
                            {hasTotp && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">aktiv</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Google Authenticator, 1Password, Authy via QR-Code — funktioniert auch offline.
                        </p>
                    </div>
                    {hasTotp ? (
                        <Button variant="outline" size="sm" disabled={loading} onClick={() => { setDisableMode('totp'); setPwForDisable(''); }} data-testid="2fa-remove-totp">
                            Entfernen
                        </Button>
                    ) : (
                        <Button size="sm" disabled={loading} onClick={startSetup('totp')} data-testid="2fa-add-totp">
                            Aktivieren
                        </Button>
                    )}
                </div>
                <div className="flex items-start gap-3 rounded-lg border p-3" data-testid="2fa-method-email">
                    <div className={`p-2 rounded-md ${hasEmail ? 'bg-emerald-100' : 'bg-muted'}`}>
                        <Mail className={`h-4 w-4 ${hasEmail ? 'text-emerald-700' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                        <div className="font-medium text-sm flex items-center gap-2">
                            E-Mail-Code
                            {hasEmail && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">aktiv</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            6-stelliger Code per Mail bei jedem Login.
                        </p>
                    </div>
                    {hasEmail ? (
                        <Button variant="outline" size="sm" disabled={loading} onClick={() => { setDisableMode('email'); setPwForDisable(''); }} data-testid="2fa-remove-email">
                            Entfernen
                        </Button>
                    ) : (
                        <Button size="sm" disabled={loading} onClick={startSetup('email')} data-testid="2fa-add-email">
                            Aktivieren
                        </Button>
                    )}
                </div>
            </div>
            {status.enabled && (
                <div className="flex flex-wrap gap-2 pt-2 border-t mt-2">
                    <Button variant="outline" size="sm" onClick={regenerate} disabled={loading} data-testid="2fa-regenerate">
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Neue Backup-Codes generieren
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setDisableMode('all'); setPwForDisable(''); }} disabled={loading} data-testid="2fa-disable-all">
                        2FA komplett deaktivieren
                    </Button>
                </div>
            )}
            {disableMode && (
                <div className="border-t pt-3 mt-1 flex flex-col gap-2" data-testid="2fa-disable-confirm">
                    <Label className="text-xs">
                        Zum {disableMode === 'all' ? 'Deaktivieren der 2FA' : `Entfernen der ${disableMode === 'totp' ? 'Authenticator-App' : 'E-Mail-Methode'}`}: aktuelles Passwort
                    </Label>
                    <div className="flex gap-2 flex-wrap">
                        <Input
                            type="password" value={pwForDisable}
                            onChange={(e) => setPwForDisable(e.target.value)}
                            placeholder="Passwort"
                            className="flex-1 min-w-[180px]"
                            data-testid="2fa-disable-pw"
                        />
                        <Button variant="destructive" size="sm" onClick={disable2FA} disabled={loading || !pwForDisable} data-testid="2fa-disable-confirm-btn">
                            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                            Bestätigen
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setDisableMode(null); setPwForDisable(''); }}>Abbrechen</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
